/**
 * useBotTriage — TanStack Query hooks for Bot Triagem (Story E9.2.3).
 *
 * 2 grupos:
 * - Portal partner: get state / answer / skip.
 * - Admin internal: config (GET/PATCH) + menu-tree.
 *
 * Polling de state acompanha o mesmo ciclo do useMessages portal (5s) —
 * UI mantém botões atualizados conforme bot responde / é encerrado.
 * Estados terminais (ROUTED/FALLBACK) PARAM o poll (refetchInterval=false).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { botTriageAdminApi, botTriagePortalApi } from "@/services/botTriageApi";
import type { BotTriageAnswerRequest, BotTriageConfigUpdateRequest } from "@/types/botTriage";

const KEYS = {
  portalState: (sessionId: string) => ["bot-triage", "portal", "state", sessionId] as const,
  adminConfig: () => ["bot-triage", "admin", "config"] as const,
  adminMenuTree: () => ["bot-triage", "admin", "menu-tree"] as const,
  portalMessages: (sessionId: string) => ["portal-atendimentos", "messages", sessionId] as const,
  portalList: () => ["portal-atendimentos", "list"] as const,
};

// ---------------------------------------------------------------------------
// Portal hooks
// ---------------------------------------------------------------------------

/** Lê state da triagem. `null` = sem triagem; UI esconde painel. */
export const useBotTriageState = (sessionId: string | null) =>
  useQuery({
    queryKey: sessionId
      ? KEYS.portalState(sessionId)
      : (["bot-triage", "portal", "state", "_noop"] as const),
    queryFn: () => botTriagePortalApi.getState(sessionId as string),
    enabled: sessionId !== null,
    refetchInterval: (query) => {
      // Para de pollar quando terminal — UI já atualizou.
      const data = query.state.data;
      if (data == null) return false;
      if (data.status !== "ACTIVE") return false;
      return 5_000;
    },
  });

export const useAnswerBotTriage = (sessionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BotTriageAnswerRequest) => botTriagePortalApi.answer(sessionId, body),
    onMutate: async () => {
      // Cancela polls em voo pra evitar response antiga sobrescrever.
      await Promise.all([
        qc.cancelQueries({ queryKey: KEYS.portalState(sessionId) }),
        qc.cancelQueries({ queryKey: KEYS.portalMessages(sessionId) }),
      ]);
    },
    onSuccess: (data) => {
      qc.setQueryData(KEYS.portalState(sessionId), data);
      // Mensagens novas (PARTNER echo + SYSTEM next) chegam via poll
      // — invalidate força refresh imediato.
      qc.invalidateQueries({ queryKey: KEYS.portalMessages(sessionId) });
      // sector_id da sessão pode ter mudado (ROUTED) → invalida lista.
      qc.invalidateQueries({ queryKey: KEYS.portalList() });
    },
  });
};

export const useSkipBotTriage = (sessionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => botTriagePortalApi.skip(sessionId),
    onMutate: async () => {
      await Promise.all([
        qc.cancelQueries({ queryKey: KEYS.portalState(sessionId) }),
        qc.cancelQueries({ queryKey: KEYS.portalMessages(sessionId) }),
      ]);
    },
    onSuccess: (data) => {
      qc.setQueryData(KEYS.portalState(sessionId), data);
      qc.invalidateQueries({ queryKey: KEYS.portalMessages(sessionId) });
      qc.invalidateQueries({ queryKey: KEYS.portalList() });
    },
  });
};

// ---------------------------------------------------------------------------
// Admin hooks
// ---------------------------------------------------------------------------

export const useBotTriageConfig = () =>
  useQuery({
    queryKey: KEYS.adminConfig(),
    queryFn: () => botTriageAdminApi.getConfig(),
    staleTime: 30_000,
  });

export const useUpdateBotTriageConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BotTriageConfigUpdateRequest) => botTriageAdminApi.patchConfig(body),
    onSuccess: (data) => {
      qc.setQueryData(KEYS.adminConfig(), data);
    },
  });
};

export const useBotTriageMenuTree = (enabled: boolean = true) =>
  useQuery({
    queryKey: KEYS.adminMenuTree(),
    queryFn: () => botTriageAdminApi.getMenuTree(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
